import { useMemo } from "react";
import {
  useFirestoreOrderBy,
  useFirestoreGeneralIn,
} from "../../firebase/useFirestore.js";
import buildAdressLabelMap from "../../helpers/adressHelpers/buildAdressLabelMap.js";
import { ADDRESS_LABEL_TITLES } from "../../helpers/adressHelpers/householdFields.js";
import notDeleted from "../../helpers/notDeleted.js";

/**
 * Link a task to a household.
 *
 * Without this, an appointment made anywhere but the household page (the whole
 * mobile agenda, for one) keeps AdressID empty and never shows up in that
 * household's task overview.
 *
 * Mounted only when its section is open — both hooks read the full tenant list.
 *
 * @param {object} props
 * @param {string|null} props.value - Currently linked Adresses.ID
 * @param {(adressId: string|null) => void} props.onChange
 */
const TaskHouseholdPicker = ({ value, onChange }) => {
  const adresses = useFirestoreOrderBy("Adresses", "Position", "asc");
  // Oude regels alleen nog als terugval tot de migratie; de vaste gegevens
  // staan op het huishouden. AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T7)
  const adressFields = useFirestoreGeneralIn("PersonaData", "Title", ADDRESS_LABEL_TITLES);

  const labels = useMemo(() => buildAdressLabelMap(adressFields, adresses), [adressFields, adresses]);

  const options = useMemo(() => {
    const list = Array.isArray(adresses) ? adresses : [];

    return list
      // Soft delete (ADR-008): verwijderde huishoudens niet aanbieden.
      .filter(notDeleted)
      .filter((adress) => adress?.ID)
      .map((adress) => ({
        id: adress.ID,
        label: labels[adress.ID] || adress.Title || "Naamloos huishouden",
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "nl"));
  }, [adresses, labels]);

  return (
    <div>
      <label>
        <strong>Huishouden</strong>
        <select
          className="task-editor-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
        >
          <option value="">Geen huishouden</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <p className="task-editor-household-hint">
        Koppel de afspraak aan een huishouden om hem in het takenoverzicht van
        dat huishouden te laten zien.
      </p>
    </div>
  );
};

export default TaskHouseholdPicker;
